import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3,
  PlusCircle,
  Briefcase,
  BookOpen,
  LineChart,
  Bot,
  Building2,
  Activity,
  FolderTree,
  Shield,
  Settings,
  Bell,
  Search,
  LogOut,
  User as UserIcon,
  ChevronDown,
  LayoutGrid,
  Command,
  Cpu,
  Menu,
  X
} from 'lucide-react';
import { cn } from './lib/utils';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserPreferencesProvider, useUserPreferences } from './contexts/UserPreferencesContext';
import { FeatureFlagProvider, useFeatureFlags, Module } from './contexts/FeatureFlagContext';
import { TenantConfigProvider, useTenantConfig } from './contexts/TenantConfigContext';
import { BusinessDateProvider } from './contexts/BusinessDateContext';
import { ToastProvider } from './components/shared/Toast';
import { ConfirmationProvider } from './components/shared/ConfirmationModal';
import ErrorBoundary from './components/shared/ErrorBoundary';
import { useRole } from './hooks/useRole';
import RecipientsPage from './features/abf/reporting/RecipientsPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleProtectedRoute from './components/auth/RoleProtectedRoute';
import ModuleProtectedRoute from './components/auth/ModuleProtectedRoute';
import LoginScreen from './components/auth/LoginScreen';
import RegisterScreen from './components/auth/RegisterScreen';
import ForgotPasswordScreen from './components/auth/ForgotPasswordScreen';
import ResetPasswordScreen from './components/auth/ResetPasswordScreen';
import SsoCallback from './components/auth/SsoCallback';
import ReadOnlyBanner from './components/shared/ReadOnlyBanner';
import GlobalContextStrip from './components/shared/GlobalContextStrip';
import { AxiomFlowMark } from './components/shared/BrandLogo';
import DesignSystemScreen from './components/DesignSystemScreen';
import BondTransactionScreen from './components/BondTransactionScreen';
import InstitutionalTradeEntry from './components/institutional/InstitutionalTradeEntry';
import RetailTradeEntry from './components/retail/RetailTradeEntry';
import SecurityManagementScreen from './components/SecurityManagementScreen';
import IssuerProfile from './components/IssuerProfile';
import HoldingsScreen from './components/HoldingsScreen';
import MarketDataScreen from './components/MarketDataScreen';
import ReferenceDataScreen from './components/ReferenceDataScreen';
import DashboardScreen from './components/DashboardScreen';
import IBORDashboardScreen from './components/IBORDashboardScreen';
import AnalyticsScreen from './components/AnalyticsScreen';
import ComplianceDashboardScreen from './components/ComplianceDashboardScreen';
import InsuranceALMDashboardScreen from './components/InsuranceALMDashboardScreen';
import InsuranceAccountingScreen from './components/InsuranceAccountingScreen';
import FixMonitoringScreen from './components/FixMonitoringScreen';
import UserPreferencesScreen from './components/UserPreferencesScreen';
import LandingPage from './components/LandingPage';
import OMSLandingPage from './components/OMSLandingPage';
import ABFLandingPage from './components/ABFLandingPage';
import AdminHub from './components/AdminHub';
import AIAgentsHub from './components/AIAgentsHub';
import AuditTrailViewer from './components/AuditTrailViewer';
import EMSXIntegrationScreen from './components/EMSXIntegrationScreen';
import MiddleOfficeManagementHub from './components/MiddleOfficeManagementHub';
import N8nWorkflowManagementScreen from './components/N8nWorkflowManagementScreen';
import TaxHarvestingScreen from './components/TaxHarvestingScreen';
import { OMSDashboard, CommandAction } from './components/os';
import AutonomousAgentsDashboard from './components/AutonomousAgentsDashboard';
import QuickSearch from './components/QuickSearch';
import KeyboardShortcutsOverlay from './components/KeyboardShortcutsOverlay';
// Retail screens
import HouseholdsScreen from './components/retail/HouseholdsScreen';
import ModelPortfoliosScreen from './components/retail/ModelPortfoliosScreen';
import FeeBillingScreen from './components/retail/FeeBillingScreen';
import SleeveUMAScreen from './components/retail/SleeveUMAScreen';
import ClientOnboardingScreen from './components/retail/ClientOnboardingScreen';
import PortfolioHealthScreen from './components/retail/PortfolioHealthScreen';
import RetailAnalyticsHub from './components/retail/RetailAnalyticsHub';
import ClientStatementsScreen from './components/retail/ClientStatementsScreen';
import DocumentInboxScreen from './components/retail/DocumentInboxScreen';
import AlternativeAssetsScreen from './components/retail/AlternativeAssetsScreen';
import FundWindDownScreen from './components/alternatives/FundWindDownScreen';
import SecondaryPurchaseScreen from './components/alternatives/SecondaryPurchaseScreen';
// CovenantHeatmapScreen retired Task #13 — heatmap path now redirects to unified Covenant Center
import NewPortcoScreen from './components/portco/NewPortcoScreen';
import GPLPRosterScreen from './components/gp/GPLPRosterScreen';
// LP Performance Center — PE/private-credit performance for LP allocators
import LPPerformanceCenterScreen from './components/pe/LPPerformanceCenterScreen';
// Risk Center — cross-asset 4-method VaR + tenant concentration (PGIM landing + Aladdin detail)
// RiskCenterScreen is now rendered inside AnalyticsScreen (Cross-Asset Risk tab),
// so it is no longer imported here.
import RiskDetailScreen from './components/risk/RiskDetailScreen';
// Alts regulatory templates (AIFMD/SFDR/BDC/Form PF §4) — Phase B3
import AltsRegulatoryReportingScreen from './components/regulatory/AltsRegulatoryReportingScreen';
// Tenant-specific Portfolio Management Hubs
import RetailPortfolioHub from './components/retail/RetailPortfolioHub';
import InstitutionalPortfolioHub from './components/institutional/InstitutionalPortfolioHub';
import FacilityWorkspacePage from './features/abf/workspace/FacilityWorkspacePage';
import AbfCopilotLayout, { AskAxiomPanel, pcCopilotContext } from './features/abf/copilot/AbfCopilotLayout';
import ContractVersionReviewPage from './features/abf/onboarding/ContractVersionReviewPage';
import AmendmentReviewPage from './features/abf/amendments/AmendmentReviewPage';
import AgreementReviewPage from './features/credit/agreementReview/AgreementReviewPage';
import DocumentTemplatesPage from './features/documentTemplates/DocumentTemplatesPage';
import DocumentTemplateVersionPage from './features/documentTemplates/DocumentTemplateVersionPage';
import Loan360Page from './components/credit/Loan360Page';
import PrivateCreditHome from './features/privateCredit/PrivateCreditHome';
import PrivateCreditWorkQueuePage from './features/privateCredit/PrivateCreditWorkQueuePage';
import PortfolioBoardPage from './features/abf/board/PortfolioBoardPage';
import AbfHome from './features/abf/AbfHome';
import {ForwardFlowDirectoryPage, ForwardFlowProgramPage} from './features/abf/forwardFlow/ForwardFlowPages';
import WorkQueuePage from './features/abf/board/WorkQueuePage';
import AccountsBoardPage from './features/abf/board/AccountsBoardPage';
import ObligorsPage from './features/abf/board/ObligorsPage';
import FacilityEntryPage from './features/abf/onboarding/entry/FacilityEntryPage';
// Settings
import SchwabIntegration from './components/settings/SchwabIntegration';

// ============================================================================
// COMMAND CENTER WRAPPER WITH NAVIGATION
// ============================================================================
const CommandCenterWithNavigation: React.FC = () => {
  const navigate = useNavigate();

  const handleCommandExecute = React.useCallback((command: CommandAction, params?: Record<string, any>) => {
    console.log('Executing command:', command.id, params);

    // Command ID to route/action mapping
    const commandRoutes: Record<string, string | (() => void)> = {
      // Trading commands
      'new-trade': '/trade-entry/fixed-income',
      'block-trade': '/trade-entry/fixed-income', // Block trades use same entry
      'quick-rfq': '/bond-transactions?action=rfq',

      // Workflow commands - navigate to blotter with status filter
      'submit-preclear': '/bond-transactions?status=DRAFT', // Show DRAFT trades to submit
      'approve-trades': '/bond-transactions?status=PRECLEAR_PENDING', // Show trades pending approval
      'send-to-venue': '/bond-transactions?status=APPROVED', // Show approved trades to send
      'post-trades': '/bond-transactions?status=EXECUTED', // Show executed trades to post

      // Compliance commands
      'compliance-check': '/bond-transactions?action=compliance',
      'view-violations': '/compliance-rules?tab=violations',
      'manage-rules': '/compliance-rules',

      // Portfolio commands
      'rebalance': '/portfolio-management?tab=rebalancing',
      'hedge-portfolio': '/portfolio-management?tab=hedging',
      'tax-harvest': '/portfolio-management?tab=tax-alpha',
      'portfolio-analysis': '/portfolio-management',

      // IBOR commands
      'view-positions': '/holdings',
      'view-tax-lots': '/ibor?tab=tax-lots',
      'mark-to-market': '/ibor?action=mtm',

      // Middle Office commands
      'reconciliation': '/middle-office?tab=reconciliation',
      'corporate-actions': '/middle-office?tab=corporate-actions',
      'cash-management': '/middle-office?tab=cash',
      'settlement-status': '/middle-office?tab=reconciliation',

      // Reporting commands
      'generate-report': '/analytics?action=report',
      'audit-log': '/admin?tab=audit',
      'ai-audit-log': '/ai-agents?tab=audit',

      // System commands
      'fix-monitor': '/fix-monitoring',
      'user-management': '/admin?tab=users',
      'settings': '/preferences',
    };

    const action = commandRoutes[command.id];

    if (typeof action === 'function') {
      action();
    } else if (action) {
      navigate(action);
    } else {
      console.warn('Unknown command:', command.id);
    }
  }, [navigate]);

  return <OMSDashboard onCommandExecute={handleCommandExecute} />;
};

// ============================================================================
// COMPONENTS
// ============================================================================

const NavTab: React.FC<{ to: string; icon: React.ReactNode; label: string; active: boolean }> = 
  ({ to, icon, label, active }) => (
    <Link 
      to={to} 
      className={cn(
        "flex shrink-0 items-center gap-1.5 px-2.5 py-2 rounded-xl text-[13px] font-medium whitespace-nowrap transition-all duration-200 relative group",
        active 
          ? "text-blue-400 bg-blue-500/10 border border-blue-500/20" 
          : "text-slate-400 hover:text-white hover:bg-white/5"
      )}
    >
      <span className={cn("transition-transform group-hover:scale-110", active && "text-blue-400")}>{icon}</span>
      {label}
      {active && (
        <motion.div 
          layoutId="nav-glow"
          className="absolute -bottom-[1px] left-2.5 right-2.5 h-[2px] bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]"
          initial={false}
        />
      )}
    </Link>
  );

const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const { tenantName, tier, isInstitutional, isRetail, hasFixConnectivity, hasTaxHarvesting } = useTenantConfig();
  const [isOpen, setIsOpen] = useState(false);

  if (!user) return null;

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-3 py-1.5 rounded-xl hover:bg-white/5 transition-all group"
      >
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center border border-white/10 text-slate-400 group-hover:text-white transition-colors">
          <UserIcon className="w-4 h-4" />
        </div>
        <div className="text-left hidden min-[1900px]:block">
          <div className="text-xs font-semibold text-white leading-tight">{user.fullName}</div>
          <div className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">{user.role}</div>
        </div>
        <ChevronDown className={cn("w-4 h-4 text-slate-500 transition-transform", isOpen && "rotate-180")} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="absolute right-0 mt-2 w-56 bg-slate-900 border border-white/10 rounded-2xl p-2 shadow-2xl z-20"
            >
              <div className="px-3 py-2 border-b border-white/5 mb-1">
                <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-1">Signed in as</div>
                <div className="text-sm text-slate-200 font-medium truncate mb-2">{user.username}</div>
                
                <div className="text-[10px] text-slate-400 space-y-1 bg-slate-950/50 p-2 rounded-lg border border-white/5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={cn("px-1.5 py-0.5 rounded text-[8px] font-bold uppercase", isRetail ? "bg-emerald-500/20 text-emerald-400" : "bg-blue-500/20 text-blue-400")}>
                      {isRetail ? 'RIA' : isInstitutional ? 'INSTITUTIONAL' : 'HYBRID'}
                    </span>
                    <span className="font-medium text-slate-300 truncate">{tenantName}</span>
                  </div>
                  <div className="opacity-80 pl-1">{tier} tier</div>
                  {isInstitutional && hasFixConnectivity && <div className="opacity-80 pl-1">FIX Enabled</div>}
                  {isRetail && hasTaxHarvesting && <div className="opacity-80 pl-1">Tax Harvesting Enabled</div>}
                </div>
              </div>
              <Link
                to="/preferences"
                onClick={() => setIsOpen(false)}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-xl transition-all"
              >
                <Settings className="w-4 h-4" />
                Settings
              </Link>
              <button 
                onClick={() => { logout(); setIsOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10 rounded-xl transition-all"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

const NavigationComponent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useRole();
  const { preferences } = useUserPreferences();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [chordHint, setChordHint] = useState<string | null>(null);
  const pendingChordRef = React.useRef<'g' | 'n' | null>(null);
  const chordTimerRef = React.useRef<number | null>(null);

  const clearChord = React.useCallback(() => {
    pendingChordRef.current = null;
    setChordHint(null);
    if (chordTimerRef.current) {
      window.clearTimeout(chordTimerRef.current);
      chordTimerRef.current = null;
    }
  }, []);

  const startChord = React.useCallback((chord: 'g' | 'n') => {
    pendingChordRef.current = chord;
    setChordHint(chord === 'g' ? 'g ... Navigate' : 'n ... New ticket');
    if (chordTimerRef.current) {
      window.clearTimeout(chordTimerRef.current);
    }
    chordTimerRef.current = window.setTimeout(clearChord, 1100);
  }, [clearChord]);

  const focusPageSearch = React.useCallback(() => {
    const selectors = [
      '[data-global-search]',
      'input[type="search"]:not([disabled])',
      'input[placeholder*="Search" i]:not([disabled])',
      'input[aria-label*="Search" i]:not([disabled])',
      'input[placeholder*="Filter" i]:not([disabled])',
    ];
    const field = document.querySelector<HTMLInputElement>(selectors.join(', '));
    if (!field) return false;
    field.focus();
    field.select?.();
    return true;
  }, []);

  const emitTicketShortcut = React.useCallback((action: string) => {
    window.dispatchEvent(new CustomEvent('axiom:ticket-shortcut', { detail: { action } }));
  }, []);

  // Bloomberg-style operator shortcuts. Guarded so form typing remains normal.
  useEffect(() => {
    const isEditableTarget = (target: EventTarget | null) => {
      const element = target as HTMLElement | null;
      if (!element) return false;
      const tagName = element.tagName;
      return element.isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
    };

    const navigateChordRoutes: Record<string, string> = {
      d: '/home',
      p: '/holdings',
      b: '/bond-transactions',
      t: '/trade-entry/fixed-income',
      c: '/compliance-dashboard',
      r: '/analytics',
      a: '/ai-agents',
      m: '/middle-office?tab=document-processing',
      l: '/portfolio-management?tab=credit-loans',
      v: '/portfolio-management?tab=alternatives',
      s: '/preferences',
    };

    const newTicketChordRoutes: Record<string, string> = {
      t: '/trade-entry/fixed-income',
      f: '/trade-entry/fixed-income',
      e: '/trade-entry/equity',
      l: '/trade-entry/leveraged-loan',
      p: '/trade-entry/private-fixed-income',
      i: '/trade-entry/derivatives/ir-swap',
      x: '/trade-entry/fx',
      r: '/trade-entry/repo',
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // Synthetic keydowns (browser autofill / extensions) can carry no key —
      // guard or every keystroke handler crashes the app (F-CCS walkthrough
      // finding 2026-08-07).
      if (!event.key) return;
      const key = event.key.toLowerCase();
      const modifier = event.metaKey || event.ctrlKey;
      const inTradeEntry = location.pathname.startsWith('/trade-entry');

      if (modifier && key === 'k') {
        event.preventDefault();
        setIsSearchOpen(prev => !prev);
        setIsShortcutsOpen(false);
        clearChord();
        return;
      }

      if (isShortcutsOpen) {
        if (event.key === 'Escape') {
          event.preventDefault();
          setIsShortcutsOpen(false);
        }
        return;
      }

      if (isSearchOpen) return;

      if (modifier && key === '/') {
        event.preventDefault();
        navigate('/ai-agents');
        clearChord();
        return;
      }

      if (modifier && event.shiftKey && key === 'o') {
        event.preventDefault();
        navigate('/bond-transactions?status=EXECUTED');
        clearChord();
        return;
      }

      if (inTradeEntry && modifier) {
        if (key === 's') {
          event.preventDefault();
          emitTicketShortcut('draft');
          return;
        }
        if (event.key === 'Enter') {
          event.preventDefault();
          emitTicketShortcut('primary');
          return;
        }
        if (key === 'd' && !event.shiftKey) {
          event.preventDefault();
          emitTicketShortcut('duplicate');
          return;
        }
        if (key === 'j' && !event.shiftKey) {
          event.preventDefault();
          emitTicketShortcut('jumpErrors');
          return;
        }
        if (key === 'l' && !event.shiftKey) {
          event.preventDefault();
          emitTicketShortcut('toggleAllocations');
          return;
        }
        if (event.shiftKey && key === 'p') {
          event.preventDefault();
          emitTicketShortcut('submitForPreClear');
          return;
        }
        if (event.shiftKey && key === 'a') {
          event.preventDefault();
          emitTicketShortcut('approvePreClear');
          return;
        }
        if (event.shiftKey && key === 'r') {
          event.preventDefault();
          emitTicketShortcut('send');
          return;
        }
        if (event.shiftKey && key === 'e') {
          event.preventDefault();
          emitTicketShortcut('execute');
          return;
        }
      }

      if (isEditableTarget(event.target)) return;

      if (event.key === '?') {
        event.preventDefault();
        setIsShortcutsOpen(true);
        clearChord();
        return;
      }

      if (key === '/') {
        event.preventDefault();
        if (!focusPageSearch()) {
          setIsSearchOpen(true);
        }
        clearChord();
        return;
      }

      const pendingChord = pendingChordRef.current;
      if (pendingChord === 'g') {
        const route = navigateChordRoutes[key];
        if (route) {
          event.preventDefault();
          navigate(route);
          clearChord();
          return;
        }
        clearChord();
      } else if (pendingChord === 'n') {
        const route = newTicketChordRoutes[key];
        if (route) {
          event.preventDefault();
          navigate(route);
          clearChord();
          return;
        }
        clearChord();
      }

      if (key === 'g') {
        event.preventDefault();
        startChord('g');
      } else if (key === 'n') {
        event.preventDefault();
        startChord('n');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    clearChord,
    emitTicketShortcut,
    focusPageSearch,
    isSearchOpen,
    isShortcutsOpen,
    location.pathname,
    navigate,
    startChord,
  ]);

  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  // Get feature flags for module-based navigation filtering
  const { isModuleEnabled } = useFeatureFlags();

  // Navigation items - Retail-specific items are integrated into other tabs,
  // so they don't appear in top-level nav (they are accessed via Reference, Portfolios, Middle Office)
  const { isRetail: isRetailTenant } = useTenantConfig();

  const allNavItems = React.useMemo(() => [
    { path: '/home', label: 'Dashboard', icon: <LayoutGrid className="w-4 h-4" /> },
    { path: '/command-center', label: 'Command Center', icon: <Command className="w-4 h-4" />, institutionalOnly: true },
    { path: '/bond-transactions', label: 'Blotter', icon: <BarChart3 className="w-4 h-4" /> },
    { path: '/trade-entry/fixed-income', label: 'Trade Entry', icon: <PlusCircle className="w-4 h-4" />, match: 'startsWith' },
    { path: '/portfolio-management', label: 'Portfolios', icon: <Briefcase className="w-4 h-4" /> },
    { path: '/ibor', label: 'IBOR', icon: <BookOpen className="w-4 h-4" /> },  // Core module - always visible
    { path: '/analytics', label: 'Analytics', icon: <LineChart className="w-4 h-4" />, match: 'startsWith' },
    { path: '/compliance-dashboard', label: 'Compliance', icon: <Shield className="w-4 h-4" />, institutionalOnly: true },
    { path: '/insurance-alm', label: 'Insurance ALM', icon: <Activity className="w-4 h-4" />, institutionalOnly: true, module: Module.INSURANCE_REPORTING },
    { path: '/insurance-accounting', label: 'Ins. Accounting', icon: <BarChart3 className="w-4 h-4" />, institutionalOnly: true, module: Module.INSURANCE_REPORTING },
    { path: '/portco', label: 'Portfolio Cos', icon: <Building2 className="w-4 h-4" />, match: 'startsWith', institutionalOnly: true },
    { path: '/pe/performance', label: 'LP Performance', icon: <LineChart className="w-4 h-4" />, match: 'startsWith', institutionalOnly: true },
    // Risk Center merged into Analytics → Cross-Asset Risk tab to avoid silo.
    // Direct deep-links to /risk and /risk/portfolios/:id still work via routes below.
    // Note: Portfolio Health is now inside Analytics tab for retail (RetailAnalyticsHub)
    { path: '/ai-agents', label: 'AI Agents', icon: <Bot className="w-4 h-4" /> },
    { path: '/autonomous-agents', label: 'Autonomous', icon: <Cpu className="w-4 h-4" />, institutionalOnly: true },
    { path: '/middle-office', label: 'Middle Office', icon: <Building2 className="w-4 h-4" />, match: 'startsWith', module: Module.MIDDLE_OFFICE },
    { path: '/market-data', label: 'Market', icon: <Activity className="w-4 h-4" /> },
    { path: '/reference-data', label: 'Reference', icon: <FolderTree className="w-4 h-4" /> },
    // Retail-specific navigation items are now integrated:
    // - Households, Models, Onboarding -> Reference tab (RetailReferenceScreen)
    // - Fee Billing -> Middle Office tab
    // - Sleeve/UMA -> Portfolios tab (RetailPortfolioHub)
    // These routes still exist for direct access but are not shown in top nav
    { path: '/admin', label: 'Admin', icon: <Shield className="w-4 h-4" />, adminOnly: true },
    { path: '/preferences', label: 'Settings', icon: <Settings className="w-4 h-4" /> },
  ], []);
  
  const navItems = React.useMemo(() => {
    // 1. Module-based filtering (feature flags)
    const filterByModules = (items: typeof allNavItems) =>
      items.filter(item => {
        // If item has a module requirement, check if it's enabled
        if (item.module && !isModuleEnabled(item.module)) {
          return false;
        }
        // Filter out institutional-only items for retail tenants
        if (item.institutionalOnly && isRetailTenant) {
          return false;
        }
        return true;
      });

    // 2. Admin security check (hard requirement)
    const filterByAdmin = (items: typeof allNavItems) =>
      items.filter(item => !item.adminOnly || isAdmin);

    // 3. If preferences haven't loaded yet, show a sensible default set
    if (!preferences || !preferences.enabledTabs) {
      const defaultPaths = ['/', '/home', '/command-center', '/bond-transactions', '/trade-entry/fixed-income', '/portfolio-management', '/analytics', '/compliance-dashboard', '/ai-agents', '/autonomous-agents', '/preferences'];
      return filterByModules(filterByAdmin(allNavItems)).filter(item =>
        defaultPaths.includes(item.path) || (item.path === '/admin' && isAdmin)
      );
    }

    // 4. If preferences exist, respect them EXACTLY.
    // If the list is empty, it means the user unchecked EVERYTHING.
    return filterByModules(allNavItems).filter(item => {
      if (item.adminOnly && !isAdmin) return false;

      // Settings and Admin should always be accessible to authorized users
      if (item.path === '/preferences') return true;
      if (item.path === '/admin' && isAdmin) return true;

      // Filter based on user's saved list
      return preferences.enabledTabs.includes(item.path);
    });
  }, [preferences, isAdmin, allNavItems, isModuleEnabled, isRetailTenant]);

  const isActive = (path: string, match?: string) => {
    if (match === 'startsWith') return location.pathname.startsWith(path);
    return location.pathname === path;
  };

  const visibleNavItems = React.useMemo(
    () => navItems.filter(item => item.path !== '/preferences'),
    [navItems]
  );

  return (
    <nav className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-white/10 h-16">
      <div className="max-w-[1800px] mx-auto h-full px-4 2xl:px-6 flex items-center justify-between">
        <div className="flex flex-1 items-center gap-5 min-w-0 overflow-hidden">
          <Link to="/home" className="flex shrink-0 items-center gap-3 group" aria-label="AxiomFlow home">
            <AxiomFlowMark size={36} className="group-hover:scale-105 transition-transform" />
            <div className="hidden lg:block leading-none">
              <div className="text-sm font-bold text-white tracking-tight uppercase">AxiomFlow</div>
              <div className="mt-1 text-[9px] text-slate-500 font-semibold tracking-[0.22em] uppercase">Credit Platform</div>
            </div>
          </Link>

          <div className="hidden min-[1200px]:flex flex-1 min-w-0 items-center gap-1 gap-y-2 flex-wrap">
            {visibleNavItems.map((item) => (
              <NavTab 
                key={item.path} 
                to={item.path} 
                icon={item.icon} 
                label={item.label} 
                active={isActive(item.path, item.match)} 
              />
            ))}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <button
            onClick={() => setIsMobileNavOpen(prev => !prev)}
            className="min-[1200px]:hidden p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            aria-label={isMobileNavOpen ? "Close navigation menu" : "Open navigation menu"}
            title={isMobileNavOpen ? "Close navigation" : "Open navigation"}
          >
            {isMobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <button
            onClick={() => setIsSearchOpen(true)}
            className="hidden md:flex h-9 w-9 min-[1900px]:w-auto items-center justify-center min-[1900px]:justify-start gap-2 min-[1900px]:px-3 bg-slate-900 border border-white/5 rounded-xl hover:bg-slate-800 hover:border-white/10 transition-colors cursor-pointer"
            aria-label="Open command palette"
            title="Command Palette (⌘K)"
          >
            <Search className="w-3.5 h-3.5 text-slate-500" />
            <span className="hidden min-[1900px]:inline text-[10px] text-slate-500 font-medium uppercase tracking-wider">Command</span>
            <kbd className="hidden min-[1900px]:inline text-[10px] text-slate-600 font-mono">⌘K</kbd>
          </button>
          <button className="relative p-2 text-slate-400 hover:text-white transition-colors">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-blue-500 rounded-full border-2 border-slate-950" />
          </button>
          <div className="w-px h-8 bg-white/10" />
          <UserMenu />
        </div>
      </div>

      <QuickSearch
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onShowShortcuts={() => setIsShortcutsOpen(true)}
      />
      <KeyboardShortcutsOverlay
        isOpen={isShortcutsOpen}
        onClose={() => setIsShortcutsOpen(false)}
      />

      <AnimatePresence>
        {chordHint && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.12 }}
            className="fixed bottom-5 left-1/2 z-[9998] -translate-x-1/2 rounded-xl border border-cyan-400/25 bg-slate-950/95 px-3 py-2 text-xs font-bold text-cyan-200 shadow-2xl"
          >
            {chordHint}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isMobileNavOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="min-[1200px]:hidden border-t border-white/10 bg-slate-950/95 backdrop-blur-xl"
          >
            <div className="px-4 py-3 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {visibleNavItems.map((item) => (
                  <Link
                    key={`mobile-${item.path}`}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium border transition-colors",
                      isActive(item.path, item.match)
                        ? "text-blue-300 bg-blue-500/10 border-blue-500/25"
                        : "text-slate-300 bg-slate-900/60 border-white/10 hover:bg-white/5"
                    )}
                  >
                    <span className="text-slate-400">{item.icon}</span>
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

/**
 * Portco-detail redirect — routes /portco/:id into the unified Portfolio
 * Companies workbench inside Alternatives. Preserves the portco id via the
 * `portcoId` query param. Part of Path B cut #1, Task #9.
 */
const PortcoDetailRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return (
    <Navigate
      to={`/portfolio-management?tab=alternatives&sub=portfolio-companies&pcs=detail&portcoId=${id ?? ''}`}
      replace
    />
  );
};

/**
 * LP fund-detail redirect — routes /pe/performance/funds/:fundId into the
 * Alternatives shell so the Aladdin-tier tearsheet keeps the GP-workbench
 * breadcrumb chrome. Preserves fundId via query string.
 */
const LPFundDetailRedirect: React.FC = () => {
  const { fundId } = useParams<{ fundId: string }>();
  return (
    <Navigate
      to={`/portfolio-management?tab=alternatives&sub=lp-fund-detail&fundId=${fundId ?? ''}`}
      replace
    />
  );
};

const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const { isInstitutional, isRetail, isLoading: tenantConfigLoading } = useTenantConfig();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/forgot-password' || location.pathname === '/reset-password';
  const isLandingPage = ['/', '/abf', '/landing', '/oms-marketing', '/oms-marketing/abf'].includes(location.pathname);
  
  // Show navigation ONLY if authenticated AND NOT on landing/auth pages
  const showNavigation = isAuthenticated && !isAuthPage && !isLandingPage;

  return (
    <div className="min-h-screen bg-slate-950 selection:bg-blue-500/30">
      {/* Dynamic background accents */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-violet-600/10 blur-[120px] rounded-full" />
      </div>

      {showNavigation && <NavigationComponent />}
      {showNavigation && <GlobalContextStrip />}
      {showNavigation && <ReadOnlyBanner />}
          
      <main className="relative z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={pageKey(location.pathname)}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" as const }}
          >
            <Routes location={location}>
              {/* Auth routes - not protected */}
              <Route path="/login" element={<LoginScreen />} />
              <Route path="/register" element={<RegisterScreen />} />
              <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
              <Route path="/reset-password" element={<ResetPasswordScreen />} />
              <Route path="/sso-callback" element={<SsoCallback />} />
              <Route path="/oms-marketing" element={<OMSLandingPage />} />
              <Route path="/oms-marketing/abf" element={<ABFLandingPage />} />
              <Route path="/abf" element={<ABFLandingPage />} />
              {/* Protected routes */}
              {/* The marketing page is the front door; a signed-in user goes straight to their dashboard. */}
              <Route path="/" element={isAuthenticated ? <Navigate to="/home" replace /> : <OMSLandingPage />} />
              <Route path="/home" element={isAuthenticated ? (
                <ProtectedRoute>
                  <DashboardScreen />
                </ProtectedRoute>
              ) : <OMSLandingPage />} />
              <Route path="/landing" element={
                <ProtectedRoute>
                  <LandingPage />
                </ProtectedRoute>
              } />
              <Route path="/bond-transactions" element={
                <ProtectedRoute>
                  <BondTransactionScreen />
                </ProtectedRoute>
              } />
              {/* Trade Entry - Route-level composition based on client type */}
              {/* Institutional clients get full asset class support, Retail gets simplified */}
              {isInstitutional ? (
                <Route path="/trade-entry/*" element={
                  <ProtectedRoute>
                    <InstitutionalTradeEntry />
                  </ProtectedRoute>
                } />
              ) : (
                <Route path="/trade-entry/*" element={
                  <ProtectedRoute>
                    <RetailTradeEntry />
                  </ProtectedRoute>
                } />
              )}
              <Route path="/tax-harvesting" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.AI_TAX_HARVESTING]}>
                    <TaxHarvestingScreen />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/reference-data" element={
                <ProtectedRoute>
                  <ReferenceDataScreen />
                </ProtectedRoute>
              } />
              {/* Legacy routes - redirect to reference-data */}
              <Route path="/portfolios/*" element={
                <ProtectedRoute>
                  <Navigate to="/portfolio-management" replace />
                </ProtectedRoute>
              } />
              <Route path="/securities" element={
                <ProtectedRoute>
                  <SecurityManagementScreen />
                </ProtectedRoute>
              } />
              <Route path="/issuers/:id" element={
                <ProtectedRoute>
                  <IssuerProfile />
                </ProtectedRoute>
              } />
              {/* Portfolio Management - Route-level composition based on client type */}
              <Route element={<ProtectedRoute><AbfCopilotLayout /></ProtectedRoute>}>
              <Route path="/portfolio-management/credit/abf" element={
                <ProtectedRoute><AbfHome /></ProtectedRoute>
              } />
              <Route path="/portfolio-management/credit/abf/board" element={
                <ProtectedRoute>
                  <PortfolioBoardPage />
                </ProtectedRoute>
              } />
              <Route path="/portfolio-management/credit/abf/forward-flow" element={
                <ProtectedRoute><ForwardFlowDirectoryPage /></ProtectedRoute>
              } />
              <Route path="/portfolio-management/credit/abf/forward-flow/programs/:programId" element={
                <ProtectedRoute><ForwardFlowProgramPage /></ProtectedRoute>
              } />
              <Route path="/portfolio-management/credit/abf/recipients" element={
                <ProtectedRoute>
                  <RecipientsPage />
                </ProtectedRoute>
              } />
              <Route path="/portfolio-management/credit/abf/queue" element={
                <ProtectedRoute>
                  <WorkQueuePage />
                </ProtectedRoute>
              } />
              <Route path="/portfolio-management/credit/abf/accounts" element={
                <ProtectedRoute>
                  <AccountsBoardPage />
                </ProtectedRoute>
              } />
              <Route path="/portfolio-management/credit/abf/obligors" element={
                <ProtectedRoute>
                  <ObligorsPage />
                </ProtectedRoute>
              } />
              {/* Fund reports live on each facility's period page now; old links land on the board. */}
              <Route path="/portfolio-management/credit/abf/fund-reports" element={<Navigate to="/portfolio-management/credit/abf/board" replace />} />
              <Route path="/portfolio-management/credit/abf/facilities/:facilityId/contract/amendments/:versionId" element={
                <ProtectedRoute>
                  <AmendmentReviewPage />
                </ProtectedRoute>
              } />
              <Route path="/portfolio-management/credit/abf/facilities/:facilityId/:workspace" element={
                <ProtectedRoute>
                  <FacilityWorkspacePage />
                </ProtectedRoute>
              } />
              </Route>
              <Route path="/portfolio-management/credit/abf/onboarding/new" element={
                <ProtectedRoute>
                  <FacilityEntryPage />
                </ProtectedRoute>
              } />
              <Route path="/portfolio-management/credit/abf/onboarding/versions/:versionId" element={
                <ProtectedRoute>
                  <ContractVersionReviewPage />
                </ProtectedRoute>
              } />
              {/* Document templates: what each document type is read into. The platform team only. */}
              <Route path="/document-templates" element={
                <RoleProtectedRoute requiredRoles={['PLATFORM_ADMIN']}>
                  <DocumentTemplatesPage />
                </RoleProtectedRoute>
              } />
              <Route path="/document-templates/:id" element={
                <RoleProtectedRoute requiredRoles={['PLATFORM_ADMIN']}>
                  <DocumentTemplateVersionPage />
                </RoleProtectedRoute>
              } />
              <Route path="/portfolio-management/credit/agreements/:jobId" element={
                <ProtectedRoute>
                  <AgreementReviewPage />
                </ProtectedRoute>
              } />
              {/* Loan 360: a private credit facility's or leveraged loan's own page, like an ABF facility's. */}
              <Route path="/portfolio-management/credit/loans/:lane/:loanId" element={
                <ProtectedRoute>
                  <Loan360Page />
                </ProtectedRoute>
              } />
              {/* The private credit home: board, register and work queue (PC workflow parity, slice 1). */}
              <Route path="/portfolio-management/credit/private-credit" element={
                <ProtectedRoute>
                  <PrivateCreditHome />
                </ProtectedRoute>
              } />
              <Route path="/portfolio-management/credit/private-credit/queue" element={
                <ProtectedRoute>
                  <AskAxiomPanel context={pcCopilotContext('queue', true)}><PrivateCreditWorkQueuePage /></AskAxiomPanel>
                </ProtectedRoute>
              } />
              <Route path="/portfolio-management" element={
                <ProtectedRoute>
                  {/* F-NS-001: wait for the tenant type to resolve before mounting
                      a hub. Mounting RetailPortfolioHub during load rewrote the
                      requested tab (e.g. ?tab=credit-loans) to its default, so a
                      fresh load / reload of an ABF deep link bounced to Holdings. */}
                  {tenantConfigLoading ? null : isInstitutional ? <InstitutionalPortfolioHub /> : <RetailPortfolioHub />}
                </ProtectedRoute>
              } />
              <Route path="/portfolio-management/rebalancing" element={
                <ProtectedRoute>
                  <Navigate to="/portfolio-management?tab=rebalancing" replace />
                </ProtectedRoute>
              } />
              <Route path="/portfolio-management/hedging" element={
                <ProtectedRoute>
                  <Navigate to="/portfolio-management?tab=hedging" replace />
                </ProtectedRoute>
              } />
              <Route path="/portfolio-management/tax-harvesting" element={
                <ProtectedRoute>
                  <Navigate to="/portfolio-management?tab=tax-alpha" replace />
                </ProtectedRoute>
              } />
              <Route path="/portfolio-management/construction" element={
                <ProtectedRoute>
                  <Navigate to="/portfolio-management?tab=construction" replace />
                </ProtectedRoute>
              } />
              <Route path="/nav-reporting" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.NAV_CALCULATION]}>
                    <MiddleOfficeManagementHub />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              {/* Legacy route for direct holdings access */}
              <Route path="/holdings" element={
                <ProtectedRoute>
                  <HoldingsScreen />
                </ProtectedRoute>
              } />
              <Route path="/market-data" element={
                <ProtectedRoute>
                  <MarketDataScreen />
                </ProtectedRoute>
              } />
              <Route path="/ibor" element={
                <ProtectedRoute>
                  <IBORDashboardScreen />
                </ProtectedRoute>
              } />
              {/* Middle Office Management Hub with sub-tabs */}
              <Route path="/middle-office" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.MIDDLE_OFFICE]}>
                    <MiddleOfficeManagementHub />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              {/* Retail/RIA Routes */}
              <Route path="/households" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.HOUSEHOLDS]}>
                    <HouseholdsScreen />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/model-portfolios" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.MODEL_PORTFOLIOS]}>
                    <ModelPortfoliosScreen />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/fee-billing" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.FEE_BILLING]}>
                    <FeeBillingScreen />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/sleeve-uma" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.SLEEVE_UMA]}>
                    <SleeveUMAScreen />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/client-onboarding" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.CLIENT_ONBOARDING]}>
                    <ClientOnboardingScreen />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              {/* Document AI & Alternative Assets */}
              <Route path="/document-inbox" element={
                <ProtectedRoute>
                  <DocumentInboxScreen />
                </ProtectedRoute>
              } />
              <Route path="/alternative-assets" element={
                <ProtectedRoute>
                  <AlternativeAssetsScreen />
                </ProtectedRoute>
              } />
              {/* PE Phase-3 wind-down workflow. The standalone J-curve route was
                  folded into Analytics → Risk → PE / Alts sleeve; the AlternativesAnalyticsScreen
                  component is removed from the bundle. */}
              <Route path="/alternatives/funds/:fundId/wind-down" element={
                <ProtectedRoute>
                  <FundWindDownScreen />
                </ProtectedRoute>
              } />
              {/* PE Phase-4 deliverable 4 — secondary purchase pricing & ledger */}
              <Route path="/alternatives/secondary-purchases" element={
                <ProtectedRoute>
                  <SecondaryPurchaseScreen />
                </ProtectedRoute>
              } />
              {/* LP Performance Center — landing (PGIM-tier) and fund detail
                  (Aladdin-tier). Powers the alts-allocator pitch: real IRR,
                  MOIC, KS-PME, Direct Alpha, cohort heatmap, J-curve. */}
              <Route path="/pe/performance" element={
                <ProtectedRoute>
                  <LPPerformanceCenterScreen />
                </ProtectedRoute>
              } />
              {/* Deep-link compat: legacy /pe/performance/funds/:fundId now redirects
                  into the Alternatives shell so the tearsheet keeps the GP-workbench
                  breadcrumb/chrome (theme-by-surface, ref: feedback_institutional_ux_standard). */}
              <Route path="/pe/performance/funds/:fundId" element={
                <ProtectedRoute>
                  <LPFundDetailRedirect />
                </ProtectedRoute>
              } />
              {/* Cross-Asset Risk Center is now folded into Analytics →
                  Cross-Asset Risk tab. /risk redirects there. The
                  per-portfolio VaR drilldown remains a deep-link route. */}
              <Route path="/risk" element={<Navigate to="/analytics/cross-asset" replace />} />
              <Route path="/risk/portfolios/:portfolioId" element={
                <ProtectedRoute>
                  <RiskDetailScreen />
                </ProtectedRoute>
              } />
              <Route path="/analytics/cross-asset" element={
                <ProtectedRoute>
                  <AnalyticsScreen />
                </ProtectedRoute>
              } />
              <Route path="/analytics/pik" element={
                <ProtectedRoute>
                  <AnalyticsScreen />
                </ProtectedRoute>
              } />
              {/* Portfolio Companies — Path B cut #1: redirected into the
                  unified Alternatives → Portfolio Companies workbench.
                  Standalone /portco/* routes kept as redirect shims so deep
                  links don't 404. /portco/new and /portco/covenants/heatmap
                  remain at original paths (heatmap retires in Task #13). */}
              <Route path="/portco" element={
                <Navigate
                  to="/portfolio-management?tab=alternatives&sub=portfolio-companies&pcs=roster"
                  replace
                />
              } />
              <Route path="/portco/new" element={
                <ProtectedRoute>
                  <NewPortcoScreen />
                </ProtectedRoute>
              } />
              {/* Task #13: standalone heatmap retired — single master Covenant
                  Center lives in Credit & Loans → Covenants. Deep links from
                  email/docs/external systems redirect into the unified surface
                  with scope=PORTCO_DEBT pre-selected. */}
              <Route path="/portco/covenants/heatmap" element={
                <Navigate
                  to="/portfolio-management?tab=credit-loans&sub=covenants&scope=PORTCO_DEBT"
                  replace
                />
              } />
              <Route path="/portco/gp-dashboard" element={
                <Navigate
                  to="/portfolio-management?tab=alternatives&sub=portfolio-companies&pcs=gp"
                  replace
                />
              } />
              <Route path="/gp/lp-roster" element={
                <ProtectedRoute>
                  <GPLPRosterScreen />
                </ProtectedRoute>
              } />
              <Route path="/portco/:id" element={
                <ProtectedRoute>
                  <PortcoDetailRedirect />
                </ProtectedRoute>
              } />
              {/* Legacy routes - redirect to Middle Office hub */}
              <Route path="/cash-management" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.MIDDLE_OFFICE]}>
                    <MiddleOfficeManagementHub />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/corporate-actions" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.CORPORATE_ACTIONS]}>
                    <MiddleOfficeManagementHub />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/emsx" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.FIX_CONNECTIVITY]}>
                    <EMSXIntegrationScreen />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              {/* Analytics - Show different screens based on tenant type */}
              {/* Institutional: Advanced Analytics (Risk Surveillance, Attribution) */}
              {/* Retail: Analytics Hub (Portfolio Health, Performance with Fees, Client Statements) */}
              <Route path="/analytics" element={
                <ProtectedRoute>
                  {isRetail ? <RetailAnalyticsHub /> : (
                    <ModuleProtectedRoute requiredModules={[Module.RISK_ANALYTICS]}>
                      <AnalyticsScreen />
                    </ModuleProtectedRoute>
                  )}
                </ProtectedRoute>
              } />
              {/* Client / LP statements — generic statement workflow
                  (DRAFT → PENDING_APPROVAL → APPROVED → PUBLISHED → DELIVERED)
                  backed by /api/statements + PDF generation via Apache PDFBox.
                  Retail uses this for monthly/quarterly account statements;
                  institutional tenants can co-opt the workflow for LP
                  capital-account delivery alongside the ILPA v2.0 reports
                  on /pe/performance/funds/:id Reports tab. */}
              <Route path="/statements" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.CLIENT_PORTAL]}>
                    <ClientStatementsScreen />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              {/* Compliance Dashboard (institutional) - What-if, Pre-Clear Queue, Overrides, Regulatory Rules */}
              <Route path="/compliance-dashboard" element={
                <ProtectedRoute>
                  <ComplianceDashboardScreen />
                </ProtectedRoute>
              } />
              {/* Insurance ALM Dashboard */}
              <Route path="/insurance-alm" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.INSURANCE_REPORTING]}>
                    <InsuranceALMDashboardScreen />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              {/* Insurance Accounting & Capital */}
              <Route path="/insurance-accounting" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.INSURANCE_REPORTING]}>
                    <InsuranceAccountingScreen />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              {/* Direct Portfolio Health route (accessible to both) */}
              <Route path="/portfolio-health" element={
                <ProtectedRoute>
                  <PortfolioHealthScreen />
                </ProtectedRoute>
              } />
              {/* Legacy routes - redirect to analytics */}
              <Route path="/risk-dashboard" element={
                <ProtectedRoute>
                  <AnalyticsScreen />
                </ProtectedRoute>
              } />
              <Route path="/performance-attribution" element={
                <ProtectedRoute>
                  <AnalyticsScreen />
                </ProtectedRoute>
              } />
              {/* Treasury Rates merged into Market Data */}
              <Route path="/portfolio-rebalancing" element={
                <ProtectedRoute>
                  <Navigate to="/portfolio-management?tab=rebalancing" replace />
                </ProtectedRoute>
              } />
              {/* Compliance Rules moved to Reference Data */}
              <Route path="/compliance-rules" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.COMPLIANCE]}>
                    <ReferenceDataScreen />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/portfolio-construction" element={
                <ProtectedRoute>
                  <Navigate to="/portfolio-management?tab=construction" replace />
                </ProtectedRoute>
              } />
              <Route path="/hedging" element={
                <ProtectedRoute>
                  <Navigate to="/portfolio-management?tab=hedging" replace />
                </ProtectedRoute>
              } />
              <Route path="/reconciliation" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.SWIFT_RECON]}>
                    <MiddleOfficeManagementHub />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/regulatory" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.REGULATORY_REPORTING]}>
                    <MiddleOfficeManagementHub />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/regulatory/alts" element={
                <ProtectedRoute>
                  <AltsRegulatoryReportingScreen />
                </ProtectedRoute>
              } />
              <Route path="/fix-monitoring" element={
                <ProtectedRoute>
                  <ModuleProtectedRoute requiredModules={[Module.FIX_CONNECTIVITY]}>
                    <FixMonitoringScreen />
                  </ModuleProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/preferences" element={
                <ProtectedRoute>
                  <UserPreferencesScreen />
                </ProtectedRoute>
              } />
              <Route path="/design-system" element={
                <ProtectedRoute>
                  <DesignSystemScreen />
                </ProtectedRoute>
              } />
              {/* Settings - Integrations (Schwab, Fidelity, etc.) */}
              <Route path="/settings/integrations" element={
                <ProtectedRoute>
                  <SchwabIntegration />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <RoleProtectedRoute requiredRoles={['ADMIN']}>
                  <AdminHub />
                </RoleProtectedRoute>
              } />
              {/* Legacy routes - redirect to admin hub */}
              <Route path="/user-management" element={
                <RoleProtectedRoute requiredRoles={['ADMIN']}>
                  <AdminHub />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/sso" element={
                <RoleProtectedRoute requiredRoles={['ADMIN']}>
                  <AdminHub />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/n8n-workflows" element={
                <RoleProtectedRoute requiredRoles={['ADMIN']}>
                  <N8nWorkflowManagementScreen />
                </RoleProtectedRoute>
              } />
              <Route path="/ai-agents" element={
                <ProtectedRoute>
                  <AIAgentsHub />
                </ProtectedRoute>
              } />
              <Route path="/audit-trail" element={
                <ProtectedRoute>
                  <AuditTrailViewer />
                </ProtectedRoute>
              } />
              <Route path="/command-center" element={
                <ProtectedRoute>
                  <CommandCenterWithNavigation />
                </ProtectedRoute>
              } />
              <Route path="/autonomous-agents" element={
                <ProtectedRoute>
                  <AutonomousAgentsDashboard />
                </ProtectedRoute>
              } />
              <Route path="/ai-showcase" element={
                <ProtectedRoute>
                  <Navigate to="/ai-agents" replace />
                </ProtectedRoute>
              } />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

/**
 * The page transition remounts everything inside it when its key changes. An ABF facility's tabs are path
 * segments (/facilities/12/collateral), so keying on the whole path made every tab click reload the facility
 * page and all its reads; the tabs of one facility share a key. Other pages keep the full path.
 */
const pageKey = (pathname: string): string =>
  pathname.replace(/^(\/portfolio-management\/credit\/abf\/facilities\/\d+)\/[^/]+$/, '$1');

const App: React.FC = () => {
  // Global guard against mouse-wheel drift (F23/F28): scrolling while a
  // <input type="number"> is focused silently increments/decrements it, so a
  // user scrolling a ticket/define/covenant form can mutate spread/floor/
  // maturity/covenant values without noticing. Blur the focused number input on
  // wheel so the page scrolls normally and the value is left untouched — one
  // handler covers every numeric field app-wide.
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      const el = e.target as HTMLElement | null;
      if (el instanceof HTMLInputElement && el.type === 'number' && el === document.activeElement) {
        el.blur();
      }
    };
    document.addEventListener('wheel', onWheel, { passive: true });
    return () => document.removeEventListener('wheel', onWheel);
  }, []);
  return (
    <ErrorBoundary>
      <AuthProvider>
        <UserPreferencesProvider>
          <FeatureFlagProvider>
            <TenantConfigProvider>
              <BusinessDateProvider>
              <ToastProvider>
                <ConfirmationProvider>
                  <Router>
                    <AppContent />
                  </Router>
                </ConfirmationProvider>
              </ToastProvider>
              </BusinessDateProvider>
            </TenantConfigProvider>
          </FeatureFlagProvider>
        </UserPreferencesProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
